import config from "../../utils/config";
import { toast } from "react-toastify";
import { CardContent, Card, Typography, Button, Avatar, Box, Grid, Chip, Divider, Alert } from "@mui/material";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";

function EventPurchaseDetails(props) {
  const { purchaseId } = useParams();

  const [purchase, setPurchase] = useState({});
  const [student, setStudent] = useState({});
  const [studentOrganization, setStudentOrganization] = useState(null);
  const [paidTransaction, setPaidTransaction] = useState(null);
  const [showConfirmPayPopup, setShowConfirmPayPopup] = useState(false);

  const isHostInvoicing = () => {
    if (!purchase.event) return false;
    return purchase.shared ? purchase.event.parentEvent?.hostInvoicing : purchase.event?.hostInvoicing;
  };

  useEffect(() => {
    props.setTitle("Event Purchase Details");
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchPurchase();
  }, [purchaseId]); // eslint-disable-line

  useEffect(() => {
    if (student.organization != null && student.organization !== purchase.organization) {
      fetchStudentOrganization();
    }
  }, [student, purchase.organization]); // eslint-disable-line

  useEffect(() => {
    if (purchase.paid && props.user.accessLevel <= 4 && !isHostInvoicing()) {
      fetchPaidTransaction(purchase._id);
    }
  }, [purchase.paid, purchase.event, purchase.shared, props.user.accessLevel]); // eslint-disable-line

  useEffect(() => {
    if (purchase.esnCard != null && purchase.esnCard !== "") {
      fetchStudentFromESNcard(purchase.esnCard);
    }
  }, [purchase.esnCard]); // eslint-disable-line

  const fetchPaidTransaction = (purchaseId) => {
    axios
      .get(
        `${config.API_ENDPOINT}/transactions/${props.organization}/search?relationId=${purchaseId}`,
        { headers: { "x-access-token": localStorage.getItem("access-token") } }
      )
      .then((res) => setPaidTransaction(res.data))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchPurchase = () => {
    axios
      .get(`${config.API_ENDPOINT}/event-purchases/${props.organization}/${purchaseId}`, {
        headers: { "x-access-token": localStorage.getItem("access-token") },
      })
      .then((res) => setPurchase(res.data))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchStudentFromESNcard = (card) => {
    axios
      .get(`${config.API_ENDPOINT}/students/esncard/${card}`)
      .then((res) => setStudent(res.data))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const fetchStudentOrganization = () => {
    axios
      .get(`${config.API_ENDPOINT}/organizations/${student.organization}`)
      .then((res) => setStudentOrganization(res.data))
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const pay = useDebouncedCallback(
    () => {
      if (purchase.event.seatsRemaining != null && purchase.event.seatsRemaining <= 0) {
        setShowConfirmPayPopup(true);
      } else {
        sendPay();
      }
    },
    500,
    { leading: true }
  );

  const sendPay = () => {
    setShowConfirmPayPopup(false);
    axios
      .post(
        `${config.API_ENDPOINT}/event-purchases/${props.organization}/${purchaseId}/pay`,
        {},
        { headers: { "x-access-token": localStorage.getItem("access-token") } }
      )
      .then(() => {
        toast.success("Success!");
        fetchPurchase();
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const performCheckin = useDebouncedCallback(
    () => {
      axios
        .post(
          `${config.API_ENDPOINT}/event-purchases/${props.organization}/${purchaseId}/checkin`,
          {},
          { headers: { "x-access-token": localStorage.getItem("access-token") } }
        )
        .then(() => {
          toast.success("Success!");
          fetchPurchase();
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const cancelCheckin = useDebouncedCallback(
    () => {
      axios
        .post(
          `${config.API_ENDPOINT}/event-purchases/${props.organization}/${purchaseId}/cancel-checkin`,
          {},
          { headers: { "x-access-token": localStorage.getItem("access-token") } }
        )
        .then(() => {
          toast.success("Success!");
          fetchPurchase();
        })
        .catch((err) => {
          switch (err.response?.status) {
            case 401:
              localStorage.removeItem("access-token");
              window.location.replace("/login");
              break;
            default:
              toast.error("An unexpected error has occurred");
              break;
          }
        });
    },
    500,
    { leading: true }
  );

  const getOptionValue = (value, type) => {
    switch (type) {
      case "select-multi":
        return Array.isArray(value) ? value.join(", ") : value; // fallback to value in case the field changed type.
      case "datetime":
        return moment(value).format("DD-MM-YYYY HH:mm");
      default:
        return value;
    }
  };

  let eventName =
    purchase.event == null
      ? ""
      : purchase.shared
      ? purchase.event.parentEvent.name
      : purchase.event.name;
  let eventOptions =
    purchase.event == null
      ? {}
      : purchase.shared
      ? purchase.event.parentEvent.options
      : purchase.event.options;

  return (
    <Box>
      <Box sx={{ mb: 4, pb: 3, borderBottom: "1px solid rgba(19, 33, 68, 0.1)" }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {purchase.event != null ? eventName : "Purchase Details"}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {moment(purchase.datetime).format("DD/MM/YYYY HH:mm")}
        </Typography>
        {purchase.paid && (
          <Box sx={{ mt: 1 }}>
            <Chip label="Paid" size="small" color="success" />
          </Box>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Personal Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Name</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Email</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.email}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>ID/Passport</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.passport}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Phone</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.phone}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Birthdate</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{moment.utc(purchase.birthdate).format("DD/MM/YYYY")}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Nationality</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.country}</Typography>
            </Grid>
            {purchase.esnCard && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>ESNcard</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {student.organization !== purchase.organization?._id ? (
                    purchase.esnCard
                  ) : (
                    <Link style={{ color: "inherit" }} to={`/edit-student/${student._id}`}>
                      {purchase.esnCard}
                    </Link>
                  )}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Purchase Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Organization</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.organization?.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Student Organization</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{studentOrganization == null ? purchase.organization?.name : studentOrganization.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Price</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{purchase.price?.toFixed(2) + " €"}</Typography>
            </Grid>
            {purchase.options != null && Object.entries(purchase.options).length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Event Options</Typography>
                <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                  {Object.entries(purchase.options).map((entry, index) => (
                    <Typography key={index} variant="body2">
                      <span style={{ fontWeight: 600 }}>{entry[0]}:</span> {getOptionValue(entry[1], eventOptions[index]?.type)}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )}
            {isHostInvoicing() && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Payments for this purchase will go to the host organization.
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {(purchase.checkedInAt != null || purchase.checkedInBy != null) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Check-in Information</Typography>
            {purchase.checkedInAt && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <span style={{ fontWeight: 600 }}>Checked-in at:</span> {moment(purchase.checkedInAt).format("DD/MM/YYYY HH:mm")}
              </Typography>
            )}
            {purchase.checkedInBy && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                <Avatar
                  src={purchase.checkedInBy.avatarURL ? `${config.IMG_ENDPOINT}/uploads/${purchase.checkedInBy.avatarURL}` : undefined}
                  sx={{ width: 40, height: 40 }}
                />
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>Checked-in by</Typography>
                  <Typography variant="body2">{purchase.checkedInBy.name} ({purchase.checkedInBy.email})</Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {props.user.accessLevel <= 4 && purchase.checkedInAt != null ? (
          <Button variant="contained" onClick={cancelCheckin}>
            Cancel Check-in
          </Button>
        ) : props.user.accessLevel <= 4 ? (
          <Button variant="contained" onClick={performCheckin}>
            Manual Check-in
          </Button>
        ) : null}

        {props.user.accessLevel <= 4 && paidTransaction && purchase.organization._id === props.organization ? (
          <Link to={`/transaction-details/${paidTransaction._id}`} style={{ textDecoration: "none" }}>
            <Button variant="contained">View Transaction</Button>
          </Link>
        ) : !purchase.paid && props.user.accessLevel <= 4 ? (
          <Button variant="contained" onClick={pay}>
            Mark as Paid
          </Button>
        ) : null}

        {props.user.accessLevel <= 4 && purchase.event != null && (
          <Link
            to={
              purchase.shared
                ? purchase.organization._id === props.organization
                  ? `/edit-shared-event/${purchase.event._id}`
                  : `/edit-event/${purchase.event.parentEvent._id}`
                : `/edit-event/${purchase.event._id}`
            }
            style={{ textDecoration: "none" }}
          >
            <Button variant="outlined">View Event</Button>
          </Link>
        )}
      </Box>

      <ConfirmationDialog
        open={showConfirmPayPopup}
        message="This event is full. Do you want to proceed anyway?"
        onYes={sendPay}
        onClose={() => setShowConfirmPayPopup(false)}
      />
    </Box>
  );
}

export default EventPurchaseDetails;
